import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useTheme } from "@/components/ui/theme-provider";
import { Link } from "react-router-dom";
import { useState } from "react";

interface ProfileFormProps {
  profile: {
    fullName: string;
    company: string;
    email: string;
    telephone: string;
    contactConsent: boolean;
    privacyConsent: boolean;
    logoConsent: boolean;
  };
  onProfileChange: (field: string, value: string | boolean) => void;
  onNext: () => void;
}

export const ProfileForm = ({ profile, onProfileChange, onNext }: ProfileFormProps) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Function to validate telephone number
  const validateTelephone = (telephone: string): boolean => {
    // Accept numbers, spaces, dashes, plus, and parentheses
    // Must be at least 10 digits (excluding formatting characters)
    const phoneRegex = /^[\d\s\-+()]{10,20}$/;
    // Count actual digits for minimum check
    const digitCount = telephone.replace(/\D/g, '').length;
    
    return phoneRegex.test(telephone) && digitCount >= 10;
  };
  
  // Handle form submission with validation
  const handleSubmit = () => {
    const errors: Record<string, string> = {};
    
    // Validate required fields
    if (!profile.fullName.trim()) {
      errors.fullName = "Full name is required";
    }
    
    if (!profile.company.trim()) {
      errors.company = "Company is required";
    }
    
    if (!profile.email.trim()) {
      errors.email = "Email is required";
    } else if (!profile.email.includes('@') || !profile.email.includes('.')) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!profile.telephone.trim()) {
      errors.telephone = "Telephone number is required";
    } else if (!validateTelephone(profile.telephone)) {
      errors.telephone = "Please enter a valid telephone number (at least 10 digits)";
    }
    
    if (!profile.privacyConsent) {
      errors.privacyConsent = "You must agree to the Privacy Policy";
    }
    
    setFormErrors(errors);
    
    // If no errors, proceed
    if (Object.keys(errors).length === 0) {
      onNext();
    }
  };
  
  return (
    <div className="space-y-6 text-foreground">
      <div className="space-y-2">
        <Label htmlFor="fullName" className="text-foreground flex items-center">
          Full Name <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="fullName"
          type="text"
          required
          value={profile.fullName}
          onChange={(e) => {
            onProfileChange("fullName", e.target.value);
            if (formErrors.fullName) {
              setFormErrors(prev => ({ ...prev, fullName: "" }));
            }
          }}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.fullName ? 'border-[#00DC82]' : ''}`}
          inputMode="text"
          autoCapitalize="words"
          placeholder="Enter your name..."
        />
        {formErrors.fullName && (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.fullName}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="company" className="text-foreground flex items-center">
          Company <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="company"
          type="text"
          required
          value={profile.company}
          onChange={(e) => {
            onProfileChange("company", e.target.value);
            if (formErrors.company) {
              setFormErrors(prev => ({ ...prev, company: "" }));
            }
          }}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.company ? 'border-[#00DC82]' : ''}`}
          inputMode="text"
          autoCapitalize="words"
          placeholder="Enter your company name..."
        />
        {formErrors.company && (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.company}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className="text-foreground flex items-center">
          Email Address <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          required
          value={profile.email}
          onChange={(e) => {
            onProfileChange("email", e.target.value);
            if (formErrors.email) {
              setFormErrors(prev => ({ ...prev, email: "" }));
            }
          }}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.email ? 'border-[#00DC82]' : ''}`}
          inputMode="email"
          autoCapitalize="none"
          autoComplete="email"
          placeholder="Enter your email address..."
        />
        {formErrors.email && (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.email}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="telephone" className="text-foreground flex items-center">
          Telephone Number <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="telephone"
          type="tel"
          required
          value={profile.telephone}
          onChange={(e) => {
            onProfileChange("telephone", e.target.value);
            if (formErrors.telephone) {
              setFormErrors(prev => ({ ...prev, telephone: "" }));
            }
          }}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.telephone ? 'border-[#00DC82]' : ''}`}
          inputMode="tel"
          autoComplete="tel"
          placeholder="Enter your telephone number..."
        />
        {formErrors.telephone ? (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.telephone}</p>
        ) : (
          <p className="text-gray-500 text-xs mt-1">Enter a valid phone number with country code</p>
        )}
      </div>

        {/* Company logo consent checkbox */}
        <div className="flex items-start gap-2">
          <Checkbox 
            id="logoConsent" 
            checked={profile.logoConsent}
            onCheckedChange={(checked) => onProfileChange("logoConsent", checked === true)}
            className="border-green-500/70 data-[state=checked]:bg-green-500 data-[state=checked]:text-white h-5 w-5 mt-0.5"
          />
          <Label 
            htmlFor="logoConsent" 
            className="text-sm text-foreground/80 cursor-pointer leading-tight"
          >
            I consent to having my company logo displayed as an early adopter partner
          </Label>
        </div>
      
      
        {/* Marketing consent checkbox */}
        <div className="flex items-start gap-2">
          <Checkbox 
            id="contactConsent" 
            checked={profile.contactConsent}
            onCheckedChange={(checked) => onProfileChange("contactConsent", checked === true)}
            className="border-green-500/70 data-[state=checked]:bg-green-500 data-[state=checked]:text-white h-5 w-5 mt-0.5"
          />
          <Label 
            htmlFor="contactConsent" 
            className="text-sm text-foreground/80 cursor-pointer leading-tight"
          >
            I agree to be contacted by Sentiri about their products and services
          </Label>
        </div>
        
    
        
        {/* Privacy policy checkbox */}
        <div className="flex items-start gap-2">
          <Checkbox 
            id="privacyConsent" 
            checked={profile.privacyConsent}
            onCheckedChange={(checked) => {
              onProfileChange("privacyConsent", checked === true);
              if (formErrors.privacyConsent && checked) {
                setFormErrors(prev => ({ ...prev, privacyConsent: "" }));
              }
            }}
            className={`border-green-500/70 data-[state=checked]:bg-green-500 data-[state=checked]:text-white h-5 w-5 mt-0.5 ${formErrors.privacyConsent ? 'border-[#00DC82]' : ''}`}
          />
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Label 
                htmlFor="privacyConsent" 
                className="text-sm text-foreground/80 cursor-pointer"
              >
                I agree to Sentiri's
              </Label>
              <Link to="/privacy" className="text-green-500 hover:underline text-sm" target="_blank">
                Privacy Policy
              </Link>
              <span className="text-[#00DC82]">*</span>
            </div>
            {formErrors.privacyConsent && (
              <p className="text-[#00DC82] text-sm">{formErrors.privacyConsent}</p>
            )}
          </div>
        </div>
      
      
      <div className="pt-2">
        <p className="text-sm text-gray-500 mb-3">
          <span className="text-[#00DC82]">*</span> Required fields
        </p>
        <Button onClick={handleSubmit} className="w-full bg-[#00DC82] text-white hover:bg-[#00DC82]/90">
          Next
        </Button>
      </div>
    </div>
  );
};